import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { StorefrontSettings } from '@workspace/api-client-react';
import { getStorefrontSettings } from '@workspace/api-client-react';
import type { CartLine, StoreProduct, Variant } from '@/lib/store';
import { readCart, CART_KEY, isEasternProduct, removeOldCatalogCaches, cartLineKey } from '@/lib/store';
import { DEFAULT_STOREFRONT_SETTINGS } from '@/lib/storefront-settings';
import type { PublicHomepageSettings } from "@workspace/api-client-react";

type StorefrontBootstrap = Omit<Partial<StorefrontSettings>, "content"> & {
  content?: Partial<StorefrontSettings["content"]>;
};

function versionedAssetUrl(value: string | null | undefined): string | null {
  if (typeof value !== "string" || !value.trim()) return null;
  try {
    const url = new URL(value.trim(), window.location.origin);
    const objectId = url.pathname.match(
      /\/([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})\.(?:avif|jpe?g|png|svg|webp)$/i,
    )?.[1];
    if (objectId) url.searchParams.set("v", objectId.replaceAll("-", "").slice(0, 16));
    return value.trim().startsWith("/")
      ? `${url.pathname}${url.search}${url.hash}`
      : url.href;
  } catch {
    return value;
  }
}

function readHomepageSettingsBootstrap(): StorefrontBootstrap {
  if (typeof document === "undefined") return {};
  const node = document.getElementById("chamakpatti-homepage-settings");
  if (!node?.textContent) return {};
  try {
    const bootstrap = JSON.parse(node.textContent) as PublicHomepageSettings;
    return {
      content: {
        ...DEFAULT_STOREFRONT_SETTINGS.content,
        ...bootstrap.content,
      },
      homepageSections: bootstrap.homepageSections.map(section => ({
        ...section,
        hiddenCopyFields: section.hiddenCopyFields ?? [],
      })),
      brandOrder: bootstrap.brandOrder?.map(entry => ({ ...entry })),
      shopByBrandRotationEnabled: bootstrap.shopByBrandRotationEnabled,
      shopByBrandRotationIntervalSeconds: bootstrap.shopByBrandRotationIntervalSeconds,
      shopByBrandCopy: {
        ...DEFAULT_STOREFRONT_SETTINGS.shopByBrandCopy,
        ...bootstrap.shopByBrandCopy,
        hiddenCopyFields: bootstrap.shopByBrandCopy.hiddenCopyFields ?? [],
      },
      socialLinks: {
        ...DEFAULT_STOREFRONT_SETTINGS.socialLinks,
        ...bootstrap.socialLinks,
      },
    };
  } catch {
    return {};
  }
}

function initialStorefrontSettings(): StorefrontSettings {
  const bootstrap = readHomepageSettingsBootstrap();
  return {
    ...DEFAULT_STOREFRONT_SETTINGS,
    ...bootstrap,
    content: { ...DEFAULT_STOREFRONT_SETTINGS.content, ...bootstrap.content },
    shopByBrandCopy: {
      ...DEFAULT_STOREFRONT_SETTINGS.shopByBrandCopy,
      ...bootstrap.shopByBrandCopy,
    },
    socialLinks: {
      ...DEFAULT_STOREFRONT_SETTINGS.socialLinks,
      ...bootstrap.socialLinks,
    },
  };
}

type StoreContextValue = {
  cart: CartLine[];
  storefrontSettings: StorefrontSettings;
  settingsReady: boolean;
  settingsLoading: boolean;
  settingsError: string | null;
  refreshStorefrontSettings: () => Promise<StorefrontSettings>;
  addToCart: (product: StoreProduct, variant: Variant, quantity?: number) => boolean;
  changeQuantity: (variantId: string, quantity: number, brandSlug?: string) => void;
  removeFromCart: (variantId: string, brandSlug?: string) => void;
  clearCart: () => void;
  itemCount: number;
};
const StoreContext = createContext<StoreContextValue | null>(null);
export function StoreProvider({ children }: { children: ReactNode }) {
  const [cart, setCart] = useState<CartLine[]>(readCart);
  const [storefrontSettings, setStorefrontSettings] = useState(initialStorefrontSettings);
  const [settingsReady, setSettingsReady] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const refreshStorefrontSettings = useCallback(async () => {
    setSettingsLoading(true);
    setSettingsError(null);
    try {
      const response = await getStorefrontSettings();
      const updatedSettings = {
        ...response,
        brandOrder: Array.isArray(response.brandOrder)
          ? response.brandOrder.map(entry => ({ ...entry }))
          : DEFAULT_STOREFRONT_SETTINGS.brandOrder.map(entry => ({ ...entry })),
        shopByBrandRotationEnabled: typeof response.shopByBrandRotationEnabled === "boolean"
          ? response.shopByBrandRotationEnabled
          : DEFAULT_STOREFRONT_SETTINGS.shopByBrandRotationEnabled,
        shopByBrandRotationIntervalSeconds: typeof response.shopByBrandRotationIntervalSeconds === "number"
          ? response.shopByBrandRotationIntervalSeconds
          : DEFAULT_STOREFRONT_SETTINGS.shopByBrandRotationIntervalSeconds,
        showSoldOutProductsInLists: typeof response.showSoldOutProductsInLists === "boolean"
          ? response.showSoldOutProductsInLists
          : DEFAULT_STOREFRONT_SETTINGS.showSoldOutProductsInLists,
        heroShopBrandSlugs: Array.isArray(response.heroShopBrandSlugs)
          ? [...response.heroShopBrandSlugs]
          : [...DEFAULT_STOREFRONT_SETTINGS.heroShopBrandSlugs],
        content: {
          ...DEFAULT_STOREFRONT_SETTINGS.content,
          ...(response.content ?? {}),
          logoUrl: versionedAssetUrl(response.content?.logoUrl),
          footerLogoUrl: versionedAssetUrl(response.content?.footerLogoUrl),
          faviconUrl: versionedAssetUrl(response.content?.faviconUrl),
          termsSections: response.content?.termsSections?.map(section => ({ ...section }))
            ?? DEFAULT_STOREFRONT_SETTINGS.content.termsSections.map(section => ({ ...section })),
        },
        homepageSections: response.homepageSections?.map(section => ({
          ...section,
          hiddenCopyFields: section.hiddenCopyFields ?? [],
        })) ?? DEFAULT_STOREFRONT_SETTINGS.homepageSections,
        shopByBrandCopy: {
          ...DEFAULT_STOREFRONT_SETTINGS.shopByBrandCopy,
          ...(response.shopByBrandCopy ?? {}),
          hiddenCopyFields: response.shopByBrandCopy?.hiddenCopyFields ?? [],
        },
        socialLinks: {
          ...DEFAULT_STOREFRONT_SETTINGS.socialLinks,
          ...(response.socialLinks ?? {}),
        },
        brandMedia: response.brandMedia?.map(item => ({ ...item }))
          ?? DEFAULT_STOREFRONT_SETTINGS.brandMedia.map(item => ({ ...item })),
        whatsappScreenshotButtonLabel: typeof response.whatsappScreenshotButtonLabel === "string"
          ? response.whatsappScreenshotButtonLabel
          : DEFAULT_STOREFRONT_SETTINGS.whatsappScreenshotButtonLabel,
        whatsappScreenshotHelperText: typeof response.whatsappScreenshotHelperText === "string"
          ? response.whatsappScreenshotHelperText
          : DEFAULT_STOREFRONT_SETTINGS.whatsappScreenshotHelperText,
        whatsappScreenshotNumber: typeof response.whatsappScreenshotNumber === "string"
          ? response.whatsappScreenshotNumber
          : DEFAULT_STOREFRONT_SETTINGS.whatsappScreenshotNumber,
        whatsappScreenshotMessage: typeof response.whatsappScreenshotMessage === "string" && response.whatsappScreenshotMessage.trim()
          ? response.whatsappScreenshotMessage
          : DEFAULT_STOREFRONT_SETTINGS.whatsappScreenshotMessage,
        deliveryTimeline: typeof response.deliveryTimeline === "string" && response.deliveryTimeline.trim()
          ? response.deliveryTimeline
          : DEFAULT_STOREFRONT_SETTINGS.deliveryTimeline,
        deliveryBagMessage: typeof response.deliveryBagMessage === "string" && response.deliveryBagMessage.trim()
          ? response.deliveryBagMessage
          : DEFAULT_STOREFRONT_SETTINGS.deliveryBagMessage,
        deliveryNoteEnabled: typeof response.deliveryNoteEnabled === "boolean"
          ? response.deliveryNoteEnabled
          : DEFAULT_STOREFRONT_SETTINGS.deliveryNoteEnabled,
        deliveryTextA: typeof response.deliveryTextA === "string"
          ? response.deliveryTextA
          : DEFAULT_STOREFRONT_SETTINGS.deliveryTextA,
        deliveryTextB: typeof response.deliveryTextB === "string"
          ? response.deliveryTextB
          : DEFAULT_STOREFRONT_SETTINGS.deliveryTextB,
        deliveryWithoutPieceCountMode: response.deliveryWithoutPieceCountMode === "not-included"
          ? "not-included"
          : DEFAULT_STOREFRONT_SETTINGS.deliveryWithoutPieceCountMode,
        deliveryNotIncludedText: typeof response.deliveryNotIncludedText === "string"
          ? response.deliveryNotIncludedText
          : DEFAULT_STOREFRONT_SETTINGS.deliveryNotIncludedText,
        shippingRatePerKgBDT: typeof response.shippingRatePerKgBDT === "number"
          && Number.isFinite(response.shippingRatePerKgBDT)
          ? response.shippingRatePerKgBDT
          : DEFAULT_STOREFRONT_SETTINGS.shippingRatePerKgBDT,
        pieceCountWeightsKg: response.pieceCountWeightsKg && typeof response.pieceCountWeightsKg === "object"
          ? { ...response.pieceCountWeightsKg }
          : { ...DEFAULT_STOREFRONT_SETTINGS.pieceCountWeightsKg },
        defaultDeliveryWeightKg: typeof response.defaultDeliveryWeightKg === "number"
          && Number.isFinite(response.defaultDeliveryWeightKg)
          ? response.defaultDeliveryWeightKg
          : null,
      };
      setStorefrontSettings(updatedSettings);
      setSettingsReady(true);
      return updatedSettings;
    } catch (error) {
      console.error("Storefront settings request failed", error);
      setSettingsReady(false);
      setSettingsError('Store settings are unavailable. Refresh and try again.');
      throw new Error('Store settings are unavailable.');
    } finally {
      setSettingsLoading(false);
    }
  }, []);
  useEffect(() => { removeOldCatalogCaches(); }, []);
  useEffect(() => {
    const favicon = storefrontSettings.content.faviconUrl;
    let icon = document.querySelector<HTMLLinkElement>('link[data-storefront-favicon="true"]');
    if (!favicon) {
      icon?.remove();
      return;
    }
    if (!icon) {
      icon = document.createElement("link");
      icon.rel = "icon";
      icon.dataset.storefrontFavicon = "true";
      document.head.appendChild(icon);
    }
    icon.href = favicon;
    const faviconPath = new URL(favicon, window.location.origin).pathname.toLowerCase();
    icon.type = faviconPath.endsWith(".png")
      ? "image/png"
      : faviconPath.endsWith(".svg") ? "image/svg+xml" : "image/webp";
  }, [storefrontSettings.content.faviconUrl]);
  useEffect(() => { localStorage.setItem(CART_KEY, JSON.stringify(cart)); }, [cart]);
  useEffect(() => { void refreshStorefrontSettings().catch(() => undefined); }, [refreshStorefrontSettings]);
  const value = useMemo<StoreContextValue>(() => ({
    cart,
    storefrontSettings,
    settingsReady,
    settingsLoading,
    settingsError,
    refreshStorefrontSettings,
    addToCart: (product, variant, quantity = 1) => {
      if (!variant.available || (!product.brandSlug && !isEasternProduct(product))) return false;
      setCart(current => {
        const identity = cartLineKey({ product, variant });
        const found = current.find(line => cartLineKey(line) === identity);
        return found
          ? current.map(line => cartLineKey(line) === identity ? { ...line, quantity: line.quantity + quantity } : line)
          : [...current, { product, variant, quantity }];
      });
      return true;
    },
    changeQuantity: (variantId, quantity, brandSlug) => setCart(current => current.map(line => line.variant.id === variantId && (line.product.brandSlug ?? "ethnc") === (brandSlug ?? "ethnc") ? { ...line, quantity: Math.max(1, Math.min(10, quantity)) } : line)),
    removeFromCart: (variantId, brandSlug) => setCart(current => current.filter(line => !(line.variant.id === variantId && (line.product.brandSlug ?? "ethnc") === (brandSlug ?? "ethnc")))),
    clearCart: () => setCart([]),
    itemCount: cart.reduce((sum, line) => sum + line.quantity, 0),
  }), [cart, storefrontSettings, settingsReady, settingsLoading, settingsError, refreshStorefrontSettings]);
  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}
export function useStore() {
  const context = useContext(StoreContext);
  if (!context) throw new Error('useStore must be used inside StoreProvider');
  return context;
}